import React, { useState, useEffect } from 'react';
import { Tabs, Form, message, Typography, Space, Grid, theme } from 'antd';
import { 
  SettingOutlined, 
  BuildOutlined, 
  SafetyCertificateOutlined,
  KeyOutlined,
  NumberOutlined,
  PercentageOutlined,
  FileTextOutlined,
  MedicineBoxOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { folioSerieService, companiesService, businessActivitiesService, certificatesService, efirmaService, companyTaxService } from '../../services/api';

// Importar componentes
import CompaniesTab from './Settings/CompaniesTab';
import CertificatesTab from './Settings/CertificatesTab';
import FolioSeriesTab from './Settings/FolioSeriesTab';
import TaxesTab from './Settings/TaxesTab';
import TaxTemplatesTab from './Settings/TaxTemplatesTab';
import EfirmaTab from './Settings/EfirmaTab';
import SaludSatTab from './Settings/SaludSatTab';

// Importar modales
import CompanyModal from './Settings/modals/CompanyModal';
import CertificateModal from './Settings/modals/CertificateModal';
import FolioSerieModal from './Settings/modals/FolioSerieModal';
import TaxModal from './Settings/modals/TaxModal';
import EfirmaModal from './Settings/modals/EfirmaModal';

const { Title } = Typography;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const Settings = () => {
  const { token } = theme.useToken();
  const { user, isReadOnly } = useAuth();
  const screens = useBreakpoint();
  
  // Estados principales
  const [companies, setCompanies] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [efirmaRecords, setEfirmaRecords] = useState([]);
  const [folioSeries, setFolioSeries] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [taxCatalogs, setTaxCatalogs] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Estados de selección
  const [activeTab, setActiveTab] = useState('companies');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFolioSerie, setSelectedFolioSerie] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  
  // Estados de modales
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const [certificateModalVisible, setCertificateModalVisible] = useState(false);
  const [folioSerieModalVisible, setFolioSerieModalVisible] = useState(false);
  const [taxModalVisible, setTaxModalVisible] = useState(false);
  const [efirmaModalVisible, setEfirmaModalVisible] = useState(false);
  const [businessActivities, setBusinessActivities] = useState([]);

  // Formularios
  const [companyForm] = Form.useForm();
  const [certificateForm] = Form.useForm();
  const [efirmaForm] = Form.useForm();
  const [folioSerieForm] = Form.useForm();
  const [taxForm] = Form.useForm();

  // Efectos
  useEffect(() => {
    fetchCompanies();
    fetchTaxCatalogs();
    fetchBusinessActivities();
  }, []);

  // Funciones de fetch
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await companiesService.getCompanies();
      setCompanies(response.data.companies || []);
    } catch (error) {
      message.error('Error al cargar las empresas');
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessActivities = async () => {
    try {
      const resp = await businessActivitiesService.getBusinessActivities();
      setBusinessActivities(resp?.data?.activities || []);
    } catch (error) {
      setBusinessActivities([]);
      console.error('Error fetching business activities:', error);
    }
  };

  const fetchCertificates = async (companyId) => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const response = await certificatesService.getCertificates(companyId);
      setCertificates(response.data || []);
    } catch (error) {
      message.error('Error al cargar los certificados');
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEfirma = async (companyId) => {
    try {
      setLoading(true);
      const resp = await efirmaService.getEfirma(companyId);
      const records = resp?.data?.records || [];
      setEfirmaRecords(Array.isArray(records) ? records : []);
    } catch (error) {
      setEfirmaRecords([]);
      console.error('Error fetching efirma:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolioSeries = async (companyId) => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const response = await folioSerieService.getFolioSeries(companyId);
      setFolioSeries(response.data || []);
    } catch (error) {
      message.error('Error al cargar las series de folio');
      console.error('Error fetching folio series:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxes = async (companyId) => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const response = await companyTaxService.getTaxes(companyId);
      setTaxes(response.data.taxes || []);
    } catch (error) {
      message.error('Error al cargar los impuestos');
      console.error('Error fetching taxes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxCatalogs = async () => {
    try {
      const response = await companyTaxService.getCatalogs();
      setTaxCatalogs(response.data || {});
    } catch (error) {
      console.error('Error fetching tax catalogs:', error);
    }
  };

  // Handlers para empresas
  const handleCompanyNew = () => {
    setSelectedCompany(null);
    companyForm.resetFields();
    companyForm.setFieldsValue({ business_activity_codes: ['TAXISTA'] });
    setCompanyModalVisible(true);
  };

  const handleCompanyEdit = (company) => {
    setSelectedCompany(company);
    const codes = (company.business_activities || []).map((a) => a.code).filter(Boolean);
    companyForm.setFieldsValue({
      ...company,
      business_activity_codes: codes,
    });
    setCompanyModalVisible(true);
  };

  const handleCompanyDelete = async (id) => {
    try {
      setLoading(true);
      await companiesService.deleteCompany(id);
      message.success('Empresa eliminada exitosamente');
      fetchCompanies();
    } catch (error) {
      message.error('Error al eliminar la empresa');
      console.error('Error deleting company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySave = async (values) => {
    try {
      setLoading(true);
      if (selectedCompany) {
        await companiesService.updateCompany(selectedCompany.id, values);
        message.success('Empresa actualizada exitosamente');
      } else {
        await companiesService.createCompany(values);
        message.success('Empresa creada exitosamente');
      }
      setCompanyModalVisible(false);
      setSelectedCompany(null);
      companyForm.resetFields();
      fetchCompanies();
    } catch (error) {
      message.error('Error al guardar la empresa');
      console.error('Error saving company:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para certificados
  const handleCertificateCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company);
    if (company) {
      fetchCertificates(companyId);
    }
  };

  const handleCertificateUpload = () => {
    if (!selectedCompany) {
      message.error('Selecciona una empresa primero');
      return;
    }
    certificateForm.resetFields();
    setCertificateModalVisible(true);
  };

  const handleEfirmaUpload = () => {
    if (!selectedCompany) return;
    efirmaForm.resetFields();
    setEfirmaModalVisible(true);
  };

  const handleEfirmaSave = async (values) => {
    if (!selectedCompany) return;
    try {
      setLoading(true);
      const formData = new FormData();
      const cerFile = values.certificate?.fileList?.[0]?.originFileObj;
      const keyFile = values.private_key?.fileList?.[0]?.originFileObj;
      if (cerFile) formData.append('certificate', cerFile);
      if (keyFile) formData.append('private_key', keyFile);
      formData.append('password', values.password);

      await efirmaService.uploadEfirma(selectedCompany.id, formData);
      message.success('eFirma subida correctamente');
      setEfirmaModalVisible(false);
      efirmaForm.resetFields();
      fetchEfirma(selectedCompany.id);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al subir eFirma');
      console.error('Error uploading efirma:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEfirmaDelete = async () => {
    if (!selectedCompany) return;
    try {
      setLoading(true);
      await efirmaService.deleteEfirma(selectedCompany.id);
      message.success('eFirma eliminada correctamente');
      fetchEfirma(selectedCompany.id);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar eFirma');
      console.error('Error deleting efirma:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateDelete = async (id) => {
    if (!selectedCompany?.id) return;

    try {
      setLoading(true);
      await certificatesService.deleteCertificate(selectedCompany.id, id);
      message.success('Certificado eliminado exitosamente');
      fetchCertificates(selectedCompany.id);
    } catch (error) {
      message.error('Error al eliminar el certificado');
      console.error('Error deleting certificate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateSave = async (values) => {
    if (!selectedCompany?.id) {
      message.error('Selecciona una empresa primero');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('password', values.password);
      
      if (values.certificate && values.certificate.fileList[0]) {
        formData.append('certificate', values.certificate.fileList[0].originFileObj);
      }
      if (values.private_key && values.private_key.fileList[0]) {
        formData.append('private_key', values.private_key.fileList[0].originFileObj);
      }

      await certificatesService.uploadCertificate(selectedCompany.id, formData);
      message.success('Certificado subido exitosamente');
      setCertificateModalVisible(false);
      certificateForm.resetFields();
      fetchCertificates(selectedCompany.id);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al subir el certificado';
      message.error(errorMessage);
      console.error('Error uploading certificate:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para series de folio
  const handleFolioSerieCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company);
    if (company) {
      fetchFolioSeries(companyId);
    }
  };

  const handleFolioSerieNew = () => {
    if (!selectedCompany) {
      message.error('Selecciona una empresa primero');
      return;
    }
    setSelectedFolioSerie(null);
    folioSerieForm.resetFields();
    setFolioSerieModalVisible(true);
  };

  const handleFolioSerieEdit = (folioSerie) => {
    setSelectedFolioSerie(folioSerie);
    folioSerieForm.setFieldsValue(folioSerie);
    setFolioSerieModalVisible(true);
  };

  const handleFolioSerieDelete = async (id) => {
    if (!selectedCompany?.id) return;

    try {
      setLoading(true);
      await folioSerieService.deleteFolioSerie(selectedCompany.id, id);
      message.success('Serie de folio eliminada exitosamente');
      fetchFolioSeries(selectedCompany.id);
    } catch (error) {
      message.error('Error al eliminar la serie de folio');
      console.error('Error deleting folio serie:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFolioSerieToggleStatus = async (id) => {
    if (!selectedCompany?.id) return;

    try {
      setLoading(true);
      await folioSerieService.toggleStatus(selectedCompany.id, id);
      message.success('Estado actualizado exitosamente');
      fetchFolioSeries(selectedCompany.id);
    } catch (error) {
      message.error('Error al cambiar el estado');
      console.error('Error toggling folio serie status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFolioSerieSetAsDefault = async (id) => {
    if (!selectedCompany?.id) return;

    try {
      setLoading(true);
      await folioSerieService.setAsDefault(selectedCompany.id, id);
      message.success('Serie establecida como predeterminada');
      fetchFolioSeries(selectedCompany.id);
    } catch (error) {
      message.error('Error al establecer como predeterminada');
      console.error('Error setting as default:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFolioSerieSave = async (values) => {
    if (!selectedCompany?.id) {
      message.error('Selecciona una empresa primero');
      return;
    }

    try {
      setLoading(true);
      if (selectedFolioSerie) {
        await folioSerieService.updateFolioSerie(selectedCompany.id, selectedFolioSerie.id, values);
        message.success('Serie de folio actualizada exitosamente');
      } else {
        await folioSerieService.createFolioSerie(selectedCompany.id, values);
        message.success('Serie de folio creada exitosamente');
      }
      setFolioSerieModalVisible(false);
      setSelectedFolioSerie(null);
      folioSerieForm.resetFields();
      fetchFolioSeries(selectedCompany.id);
    } catch (error) {
      message.error('Error al guardar la serie de folio');
      console.error('Error saving folio serie:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para impuestos
  const handleTaxCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company);
    if (company) {
      fetchTaxes(companyId);
    }
  };

  const handleTaxNew = () => {
    if (!selectedCompany) {
      message.error('Selecciona una empresa primero');
      return;
    }
    setSelectedTax(null);
    taxForm.resetFields();
    setTaxModalVisible(true);
  };

  const handleTaxEdit = (tax) => {
    setSelectedTax(tax);
    taxForm.setFieldsValue({
      tax_code: tax.tax_code,
      tax_name: tax.tax_name,
      tax_type: tax.tax_type,
      tipo_factor: tax.tipo_factor,
      tasa_o_cuota: tax.tasa_o_cuota,
      is_active: tax.is_active
    });
    setTaxModalVisible(true);
  };

  const handleTaxDelete = async (tax) => {
    try {
      setLoading(true);
      await companyTaxService.deleteTax(selectedCompany.id, tax.id);
      message.success('Impuesto eliminado correctamente');
      fetchTaxes(selectedCompany.id);
    } catch (error) {
      message.error('Error al eliminar el impuesto');
      console.error('Error deleting tax:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaxToggleStatus = async (tax) => {
    try {
      setLoading(true);
      await companyTaxService.toggleStatus(selectedCompany.id, tax.id);
      message.success(`Impuesto ${tax.is_active ? 'desactivado' : 'activado'} correctamente`);
      fetchTaxes(selectedCompany.id);
    } catch (error) {
      message.error('Error al cambiar el estado del impuesto');
      console.error('Error toggling tax status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaxSave = async (values) => {
    try {
      setLoading(true);
      
      const taxData = {
        ...values,
        company_id: selectedCompany.id
      };

      if (selectedTax) {
        await companyTaxService.updateTax(selectedCompany.id, selectedTax.id, taxData);
        message.success('Impuesto actualizado correctamente');
      } else {
        await companyTaxService.createTax(selectedCompany.id, taxData);
        message.success('Impuesto creado correctamente');
      }

      setTaxModalVisible(false);
      setSelectedTax(null);
      taxForm.resetFields();
      fetchTaxes(selectedCompany.id);
    } catch (error) {
      message.error(error.response?.data?.message || 'Error al guardar el impuesto');
      console.error('Error saving tax:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para templates de impuestos
  const handleTaxTemplateCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company);
    // TaxTemplatesTab handles fetching internally on selectedCompany change
  };

  // Handlers para tabs
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (selectedCompany) {
      switch (key) {
        case 'certificates':
          fetchCertificates(selectedCompany.id);
          break;
        case 'efirma':
          fetchEfirma(selectedCompany.id);
          break;
        case 'folio-series':
          fetchFolioSeries(selectedCompany.id);
          break;
        case 'taxes':
          fetchTaxes(selectedCompany.id);
          break;
        default:
          break;
      }
    }
  };

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Space align="center">
          <SettingOutlined style={{ fontSize: '24px', color: token.colorPrimary }} />
          <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '20px' : '24px' }}>
            Configuración
          </Title>
        </Space>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange}
        size={screens.xs ? 'small' : 'large'}
      >
        <TabPane
          tab={
            <span>
              <BuildOutlined />
              {screens.xs ? '' : 'Empresas'}
            </span>
          }
          key="companies"
        >
          <CompaniesTab
            readOnly={isReadOnly}
            companies={companies}
            loading={loading}
            onEdit={handleCompanyEdit}
            onDelete={handleCompanyDelete}
            onNew={handleCompanyNew}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <SafetyCertificateOutlined />
              {screens.xs ? '' : 'Certificados'}
            </span>
          }
          key="certificates"
        >
          <CertificatesTab
            readOnly={isReadOnly}
            companies={companies}
            certificates={certificates}
            selectedCompany={selectedCompany}
            loading={loading}
            onCompanySelect={handleCertificateCompanySelect}
            onUpload={handleCertificateUpload}
            onDelete={handleCertificateDelete}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <KeyOutlined />
              {screens.xs ? '' : 'eFirma'}
            </span>
          }
          key="efirma"
        >
          <EfirmaTab
            readOnly={isReadOnly}
            companies={companies}
            efirmaRecords={efirmaRecords}
            selectedCompany={selectedCompany}
            loading={loading}
            onCompanySelect={(companyId) => {
              const company = companies.find(c => c.id === companyId);
              setSelectedCompany(company);
              fetchEfirma(companyId);
            }}
            onUpload={handleEfirmaUpload}
            onDelete={handleEfirmaDelete}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <NumberOutlined />
              {screens.xs ? '' : 'Series de Folio'}
            </span>
          }
          key="folio-series"
        >
          <FolioSeriesTab
            readOnly={isReadOnly}
            companies={companies}
            folioSeries={folioSeries}
            selectedCompany={selectedCompany}
            loading={loading}
            onCompanySelect={handleFolioSerieCompanySelect}
            onNew={handleFolioSerieNew}
            onEdit={handleFolioSerieEdit}
            onDelete={handleFolioSerieDelete}
            onToggleStatus={handleFolioSerieToggleStatus}
            onSetAsDefault={handleFolioSerieSetAsDefault}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <PercentageOutlined />
              {screens.xs ? '' : 'Impuestos'}
            </span>
          }
          key="taxes"
        >
          <TaxesTab
            readOnly={isReadOnly}
            companies={companies}
            taxes={taxes}
            selectedCompany={selectedCompany}
            loading={loading}
            onCompanySelect={handleTaxCompanySelect}
            onNew={handleTaxNew}
            onEdit={handleTaxEdit}
            onDelete={handleTaxDelete}
            onToggleStatus={handleTaxToggleStatus}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              {screens.xs ? '' : 'Plantilla de Impuestos'}
            </span>
          }
          key="tax-templates"
        >
          <TaxTemplatesTab
            readOnly={isReadOnly}
            companies={companies}
            selectedCompany={selectedCompany ? selectedCompany.id : null}
            onCompanySelect={handleTaxTemplateCompanySelect}
          />
        </TabPane>

        {/* Salud SAT: solo descarga. La carga vive en el panel fiscal, así que
            aquí no se pasa readOnly — este lugar nunca carga. */}
        <TabPane
          tab={
            <span>
              <MedicineBoxOutlined />
              {screens.xs ? '' : 'Salud SAT'}
            </span>
          }
          key="salud-sat"
        >
          <SaludSatTab companies={companies} />
        </TabPane>
      </Tabs>

      {/* Modales */}
      <CompanyModal
        visible={companyModalVisible}
        onCancel={() => {
          setCompanyModalVisible(false);
          setSelectedCompany(null);
          companyForm.resetFields();
        }}
        onFinish={handleCompanySave}
        form={companyForm}
        selectedCompany={selectedCompany}
        businessActivities={businessActivities}
        loading={loading}
      />

      <CertificateModal
        visible={certificateModalVisible}
        onCancel={() => {
          setCertificateModalVisible(false);
          certificateForm.resetFields();
        }}
        onFinish={handleCertificateSave}
        form={certificateForm}
        selectedCompany={selectedCompany}
        loading={loading}
      />

      <EfirmaModal
        visible={efirmaModalVisible}
        onCancel={() => {
          setEfirmaModalVisible(false);
          efirmaForm.resetFields();
        }}
        onFinish={handleEfirmaSave}
        form={efirmaForm}
        selectedCompany={selectedCompany}
        loading={loading}
      />

      <FolioSerieModal
        visible={folioSerieModalVisible}
        onCancel={() => {
          setFolioSerieModalVisible(false);
          setSelectedFolioSerie(null);
          folioSerieForm.resetFields();
        }}
        onFinish={handleFolioSerieSave}
        form={folioSerieForm}
        selectedFolioSerie={selectedFolioSerie}
        selectedCompany={selectedCompany}
        loading={loading}
      />

      <TaxModal
        visible={taxModalVisible}
        onCancel={() => {
          setTaxModalVisible(false);
          setSelectedTax(null);
          taxForm.resetFields();
        }}
        onFinish={handleTaxSave}
        form={taxForm}
        selectedTax={selectedTax}
        selectedCompany={selectedCompany}
        taxCatalogs={taxCatalogs}
        loading={loading}
      />
    </div>
  );
};

export default Settings;
